import { httpClient } from '@/api/httpClient';
import {
  mapApiResourceToAppointment,
  mapApiResourceToOwnerAppointment,
  mapDraftToApiPayload,
} from './appointmentMapping';
import type {
  Appointment,
  AppointmentApiResource,
  AppointmentDraft,
  OwnerAppointment,
  OwnerAppointmentApiResource,
} from './appointmentsApi.types';

const APPOINTMENTS_RESOURCE_PATH = '/appointments';
const OWN_APPOINTMENTS_RESOURCE_PATH = '/me/appointments';

export async function fetchAppointments(): Promise<Appointment[]> {
  const response = await httpClient.get<AppointmentApiResource[]>(APPOINTMENTS_RESOURCE_PATH);
  return response.data.map(mapApiResourceToAppointment);
}

export async function fetchOwnUpcomingAppointments(): Promise<OwnerAppointment[]> {
  const response = await httpClient.get<OwnerAppointmentApiResource[]>(
    OWN_APPOINTMENTS_RESOURCE_PATH
  );
  return response.data.map(mapApiResourceToOwnerAppointment);
}

export async function fetchOwnPastAppointments(): Promise<OwnerAppointment[]> {
  const response = await httpClient.get<OwnerAppointmentApiResource[]>(
    OWN_APPOINTMENTS_RESOURCE_PATH + '/past'
  );
  return response.data.map(mapApiResourceToOwnerAppointment);
}

export async function cancelOwnAppointment(appointmentIdentifier: string): Promise<OwnerAppointment> {
  const response = await httpClient.post<OwnerAppointmentApiResource>(
    OWN_APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier + '/cancel'
  );
  return mapApiResourceToOwnerAppointment(response.data);
}

export async function requestOwnAppointmentReschedule(
  appointmentIdentifier: string,
  message: string
): Promise<OwnerAppointment> {
  const response = await httpClient.post<OwnerAppointmentApiResource>(
    OWN_APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier + '/reschedule-request',
    { message }
  );
  return mapApiResourceToOwnerAppointment(response.data);
}

export async function dismissAppointmentRescheduleRequest(
  appointmentIdentifier: string
): Promise<Appointment> {
  const response = await httpClient.post<AppointmentApiResource>(
    APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier + '/reschedule-request/dismiss'
  );
  return mapApiResourceToAppointment(response.data);
}

export async function fetchOwnAppointmentCalendarFile(appointmentIdentifier: string): Promise<Blob> {
  const response = await httpClient.get<Blob>(
    OWN_APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier + '/calendar.ics',
    { responseType: 'blob' }
  );
  return response.data;
}

export async function createAppointment(appointmentDraft: AppointmentDraft): Promise<Appointment> {
  const requestPayload = mapDraftToApiPayload(appointmentDraft);
  const response = await httpClient.post<AppointmentApiResource>(
    APPOINTMENTS_RESOURCE_PATH,
    requestPayload
  );
  return mapApiResourceToAppointment(response.data);
}

export async function updateAppointment(
  appointmentIdentifier: string,
  appointmentDraft: AppointmentDraft
): Promise<Appointment> {
  const requestPayload = mapDraftToApiPayload(appointmentDraft);
  const response = await httpClient.put<AppointmentApiResource>(
    APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier,
    requestPayload
  );
  return mapApiResourceToAppointment(response.data);
}

export async function cancelAppointment(appointmentIdentifier: string): Promise<Appointment> {
  const response = await httpClient.post<AppointmentApiResource>(
    APPOINTMENTS_RESOURCE_PATH + '/' + appointmentIdentifier + '/cancel'
  );
  return mapApiResourceToAppointment(response.data);
}
